import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, Users, MapPin, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("All");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const eventTypes = ["All", "Home Buyers", "Real Estate Agents"];

  const filteredEvents = selectedType === "All" 
    ? upcomingEvents 
    : upcomingEvents.filter(event => event.type === selectedType);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b2c4a] to-[#1a4b73] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Upcoming Events & Classes
            </h1>
            <p className="text-xl text-blue-100">
              Join our educational workshops and seminars designed for home buyers and real estate professionals. 
              Expand your knowledge and grow your network.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {eventTypes.map((type) => (
              <Button
                key={type}
                variant={selectedType === type ? "default" : "outline"}
                className={selectedType === type 
                  ? "bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white" 
                  : "border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
                }
                onClick={() => setSelectedType(type)}
              >
                {type}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-600">Check back soon for upcoming {selectedType.toLowerCase()} events.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredEvents.map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow duration-200 border-none bg-white">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge 
                        variant="secondary" 
                        className={`${
                          event.type === "Real Estate Agents" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {event.type}
                      </Badge>
                      {event.ce_credits && (
                        <Badge className="bg-[#0b2c4a] text-white">
                          {event.ce_credits}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl text-gray-900 mb-2">
                      {event.title}
                    </CardTitle>
                    <p className="text-gray-600 text-sm">
                      {event.description}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <Calendar className="h-4 w-4 text-[#0b2c4a]" />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <Clock className="h-4 w-4 text-[#0b2c4a]" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <MapPin className="h-4 w-4 text-[#0b2c4a]" />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <Users className="h-4 w-4 text-[#0b2c4a]" />
                        <span>{event.capacity}</span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Topics Covered:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {event.topics.map((topic, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-[#0b2c4a] mt-1">•</span>
                            <span>{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>Instructor:</strong> {event.instructor}
                      </p>
                    </div>

                    <Button 
                      className="w-full bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                      asChild
                    >
                      <a 
                        href={event.registration_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center"
                      >
                        Register Now
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Want to Host a Custom Event?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            We offer customized workshops and seminars for real estate offices, 
            community groups, and corporate partners. Contact us to discuss your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => window.location.href = 'tel:(720) 650-7988'}
            >
              Call (720) 650-7988
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white"
              asChild
            >
              <a href="/team">Contact Our Team</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;